import { useEffect, useState, useCallback } from 'react';

import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';

import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { AccessStatus } from '@confluence/session-data/entry-points/SiteInformationQuery.ts';
import {
	CONTEXTUAL_INVITE_NUDGE,
	LIVE_PAGE_NUDGE,
} from '@confluence/onboarding-helpers/entry-points/constants/onboarding-state-constants.ts';
import {
	useGetOnboardingState,
	useSetOnboardingState,
	deserializeState,
} from '@confluence/onboarding-helpers/entry-points/hooks/useOnboardingState/onboardingState.ts';
import { memoizedGetInvitePermissions } from '@confluence/onboarding-helpers/entry-points/getInvitePermissions.ts';
import { isUnauthorizedError } from '@confluence/error-boundary/entry-points/isUnauthorizedError.ts';
import { markErrorAsHandled } from '@confluence/graphql/entry-points/markErrorAsHandled.ts';

import { useLivePageInviteNudgeActions } from '../../LivePageInviteNudge/useLivePageInviteNudgeStore.tsx';

const CONFLUENCE_KEY = 'confluence';

interface EligibilityProps {
	contentId?: string;
}

interface LivePageOnboardingNudgeEligible {
	isEligibleForLivePageNudge: boolean;
	isShowingLivePageNudge: boolean;
	isLoading: boolean;
	showLivePageInviteFlag: () => void;
}

/**
 * Determines eligibility for the Live Page Invite Nudge
 *
 * This hook handles the improved timing invite nudge functionality for live pages.
 * The nudge is triggered when users are on live pages and eligible for invitations.
 *
 * **Important**: The `showLivePageInviteFlag` function must be called when content changes
 * occur on live pages to trigger the nudge. This is typically integrated at the editor level
 * to detect when users make their first content changes.
 *
 * @returns {object} - an object containing:
 * @returns {boolean} isEligibleForLivePageNudge - Whether the user is eligible for the live page nudge
 * @returns {boolean} isShowingLivePageNudge - Whether the nudge is currently showing
 * @returns {boolean} isLoading - Whether queries are still loading
 * @returns {function} showLivePageInviteFlag - Call this when content changes to show the invite flag nudge
 */
export const useLivePageOnboardingNudgeEligible = ({
	contentId,
}: EligibilityProps): LivePageOnboardingNudgeEligible => {
	const { setHasLivePageChanges } = useLivePageInviteNudgeActions();

	const [isCurrentlyInviteEligible, setIsCurrentlyInviteEligible] = useState(false);
	const [isShowingLivePageNudge] = useState(false);
	const { accessStatus, cloudId } = useSessionData();

	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { setOnboardingState } = useSetOnboardingState();

	const hasAdminOrUseAccess =
		accessStatus === AccessStatus.LICENSED_ADMIN_ACCESS ||
		accessStatus === AccessStatus.LICENSED_USE_ACCESS;

	const {
		data: onboardingStateData,
		loading: onboardingStateLoading,
		error: onboardingStateError,
	} = useGetOnboardingState([
		...Object.values(CONTEXTUAL_INVITE_NUDGE),
		...Object.values(LIVE_PAGE_NUDGE),
	]);

	// User Eligibility Check
	const onboardingState = deserializeState(onboardingStateData);
	const { wasFirstPublishConfettiSeen, wasLivePageNudgeSeen, livePageNudgeInviteIneligible } =
		onboardingState;

	// Skip conditions for eligibility checks
	const skip =
		!hasAdminOrUseAccess ||
		onboardingStateLoading ||
		!contentId ||
		wasLivePageNudgeSeen ||
		livePageNudgeInviteIneligible ||
		// deconflict with non live page contextual nudge
		wasFirstPublishConfettiSeen;

	const hasNoInvitePermissions = !onboardingStateLoading && livePageNudgeInviteIneligible;

	const isLoading = !!onboardingStateLoading;
	const error = onboardingStateError;

	if (error) {
		if (isUnauthorizedError(error)) {
			markErrorAsHandled(error);
		}
	}

	useEffect(() => {
		if (skip || isLoading || !!error || hasNoInvitePermissions) {
			return;
		}

		const getInvitePermissions = async () => {
			const response = await memoizedGetInvitePermissions(cloudId);
			if (response instanceof Error) {
				createAnalyticsEvent({
					type: 'sendTrackEvent',
					data: {
						action: 'failed',
						actionSubject: 'getInvitePermissions',
						source: 'livePages',
						attributes: {
							message: response?.message,
						},
					},
				}).fire();
			}

			const canInviteToConfluence: boolean = [
				...(response?.directInvite ?? []),
				...(response?.invitePendingApproval ?? []),
			].some((item) => item.includes(CONFLUENCE_KEY));

			if (canInviteToConfluence) {
				setIsCurrentlyInviteEligible(true);
			} else {
				void setOnboardingState({
					key: LIVE_PAGE_NUDGE.LIVE_PAGE_INVITE_INELIGIBLE,
					value: 'true',
				});
			}
		};
		void getInvitePermissions();
	}, [
		isLoading,
		error,
		skip,
		cloudId,
		hasNoInvitePermissions,
		setOnboardingState,
		createAnalyticsEvent,
	]);

	const isEligibleForLivePageNudge =
		!isLoading && !skip && !error && !hasNoInvitePermissions && isCurrentlyInviteEligible;

	const showLivePageInviteFlag = useCallback(() => {
		if (isEligibleForLivePageNudge) {
			setHasLivePageChanges(true);

			void setOnboardingState({
				key: LIVE_PAGE_NUDGE.LIVE_PAGE_CONTEXTUAL_NUDGE_SEEN,
				value: 'true',
			});
		}
	}, [isEligibleForLivePageNudge, setHasLivePageChanges, setOnboardingState]);

	return {
		isLoading,
		isEligibleForLivePageNudge,
		isShowingLivePageNudge,
		showLivePageInviteFlag,
	};
};
